import { AssociatedContentSelectionFragment } from 'app/graphql/__generated__/types/contento';
import {
    createListTrackingOnClick,
    createTrackingOnItemImpression,
} from 'app/utils/statistics/clientStatistics';
import { NextRouter, useRouter } from 'next/router';
import React, { memo } from 'react';
import MissingContent from '../../../../ui-components/MissingContent/MissingContent';
import { generateListTracking } from '../../../../utils/contentoUtils';
import { SectionContainer } from './AssociatedContent.styles';
import AssociatedContentItem from './AssociatedContentItem';
import AssociatedContentSection from './AssociatedContentSection';

export const isSectionDefaultExpanded = (routerQuery, section, selectedVideoId) =>
    routerQuery.tab === section.id ||
    section.items.some(({ item }) => item.videoSvtId === selectedVideoId) ||
    section.selectionType === 'upcoming';

export const shouldShowAllByDefault = (selectedVideoId, section, maxItems) =>
    section.items.findIndex(({ item }) => item.videoSvtId === selectedVideoId) >= maxItems;

export type AssociatedContentProps = {
    selections: AssociatedContentSelectionFragment[];
    selectedVideoId: string;
    progress: number | null;
    router: NextRouter;
};

const AssociatedContentInner = ({
    selections,
    selectedVideoId,
    progress,
    router,
}: AssociatedContentProps) => (
    <SectionContainer>
        {selections && selections.length ? (
            selections.map((section) => {
                const isUpcoming = section.id === 'upcoming';
                const defaultExpanded = isSectionDefaultExpanded(router.query, section, selectedVideoId);
                const maxItems = isUpcoming ? 1 : 10;
                const defaultShowAll = shouldShowAllByDefault(selectedVideoId, section, maxItems);

                return (
                    <AssociatedContentSection
                        key={section.id}
                        name={section.name}
                        defaultExpanded={defaultExpanded}
                        id={section.id}
                        selectedVideoId={selectedVideoId}
                        max={maxItems}
                        defaultShowAll={defaultShowAll}
                    >
                        {section.items.map((content, i) => {
                            const listTracking = generateListTracking(section);
                            const onItemImpression = createTrackingOnItemImpression(listTracking);
                            const onItemClick = createListTrackingOnClick(listTracking);

                            return (
                                <AssociatedContentItem
                                    key={`associated-${i}`}
                                    index={i}
                                    content={content}
                                    type={section.id}
                                    selectedVideoId={selectedVideoId}
                                    progress={progress}
                                    trackImpression={onItemImpression}
                                    trackClick={(e, leadsToPlayable) => {
                                        onItemClick(e, i + 1, content.item.svtId, leadsToPlayable);
                                    }}
                                />
                            );
                        })}
                    </AssociatedContentSection>
                );
            })
        ) : (
            <MissingContent />
        )}
    </SectionContainer>
);
const areEqual = (prevProps, nextProps) => nextProps.router.query.modalId;

const AssociatedContent = memo(AssociatedContentInner, areEqual);

const AssociatedContentContainer = ({ selections, selectedVideoId, progress }) => {
    const router = useRouter();

    return (
        <AssociatedContent
            selections={selections}
            selectedVideoId={selectedVideoId}
            progress={progress}
            router={router}
        />
    );
};

export default AssociatedContentContainer;
